import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import StatCard from "@/components/StatCard";
import NotificationSender from "@/components/NotificationSender";
import NotificationsAdminSummary from "@/components/NotificationsAdminSummary";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Users,
  BarChart3,
  Building2,
  Cpu,
  Activity,
  Archive,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { sanitizeHtml, markdownToHtml } from "@/lib/utils";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import TimeseriesChart from "@/components/TimeseriesChart";

// Staff Management Component
const StaffManagement = ({ token }) => {
  const { t } = useTranslation();
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, staffId: null, staffName: "" });
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
  });

  const loadStaff = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await api.staffList(token);
      setStaffUsers(res.staffUsers || []);
    } catch (e) {
      toast({
        title: t('system.common.loadErrorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
  }, [token]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.firstName || !formData.lastName) {
      toast({
        title: t('system.staff.allFieldsRequired'),
        variant: "destructive",
      });
      return;
    }

    try {
      setCreating(true);
      await api.staffCreate(token, formData);
      toast({
        title: t('system.staff.createSuccess'),
        description: t('system.staff.invitationSent'),
      });
      setFormData({ email: "", firstName: "", lastName: "" });
      loadStaff();
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (staffId) => {
    try {
      await api.staffToggle(token, staffId);
      toast({ title: t('system.staff.statusChanged') });
      loadStaff();
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (staff) => {
    setDeleteDialog({
      open: true,
      staffId: staff.id,
      staffName: `${staff.firstName} ${staff.lastName}`,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await api.staffDelete(token, deleteDialog.staffId);
      toast({ title: t('system.staff.deleted') });
      setDeleteDialog({ open: false, staffId: null, staffName: "" });
      loadStaff();
    } catch (e) {
      toast({
        title: t('system.staff.deleteErrorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, staffId: null, staffName: "" });
  };

  const handleResendInvitation = async (staffId) => {
    try {
      await api.staffResendInvitation(token, staffId);
      toast({ title: t('system.staff.invitationResent') });
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('system.staff.newStaff')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs text-muted-foreground">{t('system.staff.email')}</label>
              <input
                type="email"
                className="border rounded px-2 py-1 text-sm w-full"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('system.staff.emailPlaceholder')}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t('system.staff.firstName')}</label>
              <input
                type="text"
                className="border rounded px-2 py-1 text-sm w-full"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t('system.staff.lastName')}</label>
              <input
                type="text"
                className="border rounded px-2 py-1 text-sm w-full"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </div>
            <div>
              <Button type="submit" disabled={creating}>
                {creating ? "..." : t('system.staff.createInvite')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('system.staff.listTitle', { count: staffUsers.length })}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
          ) : staffUsers.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t('system.staff.none')}</div>
          ) : (
            <div className="space-y-2">
              {staffUsers.map((staff) => (
                <div
                  key={staff.id}
                  className="flex items-center justify-between p-3 border rounded hover:bg-muted"
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {staff.firstName} {staff.lastName}
                    </div>
                    <div className="text-xs text-muted-foreground">{staff.email}</div>
                    <div className="text-xs text-muted-foreground">
                      {t('system.staff.createdLabel')}: {new Date(staff.createdAt).toLocaleDateString("de-DE")}
                      {!staff.hasPassword && (
                        <span className="ml-2 text-orange-600 font-medium">
                          • {t('system.staff.passwordNotSet')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        staff.active
                          ? "bg-green-100 text-green-800"
                          : "bg-muted text-foreground"
                      }`}
                    >
                      {staff.active ? t('system.staff.active') : t('system.staff.inactive')}
                    </span>
                    {!staff.hasPassword && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResendInvitation(staff.id)}
                        title={t('system.staff.resendInviteTitle')}
                      >
                        {t('system.staff.resendEmail')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggle(staff.id)}
                    >
                      {staff.active ? t('system.staff.deactivate') : t('system.staff.activate')}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteClick(staff)}
                    >
                      {t('system.staff.delete')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && handleDeleteCancel()}
        type="danger"
        title={t('system.staff.confirmDeleteTitle')}
        description={
          <>
            {t('system.staff.confirmDeleteText', { name: deleteDialog.staffName })}
            <br /><br />
            <span className="text-red-600 font-medium">
              ⚠️ Diese Aktion kann nicht rückgängig gemacht werden.
            </span>
            <br />
            {t('system.staff.confirmDeleteNote')}
          </>
        }
        confirmText={t('system.staff.confirmDeleteConfirm')}
        cancelText={t('common.cancel')}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleting}
      />
    </div>
  );
};

// Feature Pricing Management Component
const FeaturePricingManagement = ({ token }) => {
  const { t } = useTranslation();
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [editForm, setEditForm] = useState({ monthlyPrice: '', description: '', active: true });

  const loadPricing = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const data = await api.systemFeaturePricing();
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.pricing)
        ? data.pricing
        : [];
      setPricing(items);
    } catch (e) {
      toast({
        title: 'Fehler beim Laden',
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricing();
  }, [token]);

  const startEdit = (feature) => {
    setEditingFeature(feature.featureName);
    setEditForm({
      monthlyPrice: feature.monthlyPrice.toString(),
      description: feature.description || '',
      active: feature.active
    });
  };

  const cancelEdit = () => {
    setEditingFeature(null);
    setEditForm({ monthlyPrice: '', description: '', active: true });
  };

  const saveEdit = async () => {
    if (!editingFeature) return;
    try {
      await api.systemFeaturePricingUpdate(editingFeature, {
        monthlyPrice: parseFloat(editForm.monthlyPrice),
        description: editForm.description || null,
        active: editForm.active
      });
      toast({
        title: 'Gespeichert',
        description: 'Feature-Preis wurde aktualisiert'
      });
      setEditingFeature(null);
      loadPricing();
    } catch (e) {
      toast({
        title: 'Fehler',
        description: e.message,
        variant: "destructive",
      });
    }
  };

  const getFeatureDisplayName = (featureName) => {
    const names = {
      stempelMirEin: 'StempelMirEin (Digitale Stempelkarten)',
      newsletter: 'Newsletter (SendmirEin)'
    };
    return names[featureName] || featureName;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Feature-Preise</CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Konfiguriere die monatlichen Preise für zusätzliche Features, die Agenturen für ihre Organisationen aktivieren können.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Laden...</div>
          ) : pricing.length === 0 ? (
            <div className="text-sm text-muted-foreground">Keine Features konfiguriert</div>
          ) : (
            <div className="space-y-4">
              {pricing.map((feature) => (
                <div
                  key={feature.featureName}
                  className="border rounded-lg p-4 hover:bg-muted"
                >
                  {editingFeature === feature.featureName ? (
                    <div className="space-y-4">
                      <div className="font-medium text-sm">
                        {getFeatureDisplayName(feature.featureName)}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Monatspreis (€)</label>
                          <input
                            type="number"
                            step="0.01"
                            className="w-full border rounded px-3 py-2"
                            value={editForm.monthlyPrice}
                            onChange={(e) => setEditForm({ ...editForm, monthlyPrice: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Status</label>
                          <select
                            className="w-full border rounded px-3 py-2"
                            value={editForm.active}
                            onChange={(e) => setEditForm({ ...editForm, active: e.target.value === 'true' })}
                          >
                            <option value="true">Aktiv</option>
                            <option value="false">Inaktiv</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Beschreibung</label>
                        <textarea
                          className="w-full border rounded px-3 py-2"
                          rows="2"
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          placeholder="Optional: Was ist in diesem Feature enthalten?"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveEdit}>Speichern</Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>Abbrechen</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {getFeatureDisplayName(feature.featureName)}
                        </div>
                        {feature.description && (
                          <div className="text-xs text-muted-foreground mt-1">{feature.description}</div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1">
                          Erstellt: {new Date(feature.createdAt).toLocaleDateString("de-DE")}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">
                            {parseFloat(feature.monthlyPrice).toFixed(2)} €
                          </div>
                          <div className="text-xs text-muted-foreground">pro Monat</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs px-2 py-1 rounded ${
                              feature.active
                                ? "bg-green-100 text-green-800"
                                : "bg-muted text-foreground"
                            }`}
                          >
                            {feature.active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                          <Button size="sm" variant="outline" onClick={() => startEdit(feature)}>
                            Bearbeiten
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Audit Logs Component
const AuditLogs = ({ token }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    limit: 50,
    offset: 0,
    action: "",
  });

  const loadLogs = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const data = await api.staffAuditLogs(token, filters);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast({
        title: t('system.common.loadErrorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [token, filters]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("de-DE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case "switch_organization":
        return "bg-blue-100 text-blue-800";
      case "view_campaign":
        return "bg-green-100 text-green-800";
      case "edit_settings":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-muted text-foreground";
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Staff Audit Logs ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && <div className="text-center py-4">{t('common.loading')}</div>}
          {!loading && logs.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              {t('system.audit.none')}
            </div>
          )}
          {!loading && logs.length > 0 && (
            <div className="space-y-2">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">{t('system.audit.thead.time')}</th>
                      <th className="text-left py-2 px-2">{t('system.audit.thead.employee')}</th>
                      <th className="text-left py-2 px-2">{t('system.audit.thead.organization')}</th>
                      <th className="text-left py-2 px-2">{t('system.audit.thead.action')}</th>
                      <th className="text-left py-2 px-2">{t('system.audit.thead.ip')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-b hover:bg-muted">
                        <td className="py-2 px-2 text-xs">
                          {formatDate(log.createdAt)}
                        </td>
                        <td className="py-2 px-2">
                          <div>
                            <div className="font-medium">
                              {log.staffuser?.firstName} {log.staffuser?.lastName}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {log.staffuser?.email}
                            </div>
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <div className="font-medium">{log.company?.name}</div>
                          <div className="text-xs text-muted-foreground">
                            ID: {log.company?.id}
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <span
                            className={`text-xs px-2 py-1 rounded ${getActionBadgeColor(
                              log.action
                            )}`}
                          >
                            {log.action}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-xs text-muted-foreground">
                          {log.ipAddress || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between items-center pt-4">
                <div className="text-sm text-muted-foreground">
                  Zeige {filters.offset + 1} bis{" "}
                  {Math.min(filters.offset + filters.limit, total)} von {total}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={filters.offset === 0}
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        offset: Math.max(0, prev.offset - prev.limit),
                      }))
                    }
                  >
                    Zurück
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={filters.offset + filters.limit >= total}
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        offset: prev.offset + prev.limit,
                      }))
                    }
                  >
                    Weiter
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const SystemDashboard = () => {
  const { t } = useTranslation();
  const { token, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [outerTab, setOuterTab] = useState("analytics");
  const [settingsTab, setSettingsTab] = useState("impressum");
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (!t) {
      setOuterTab('analytics');
      return;
    }
    const map = {
      // Org management happens in agency dashboard now; keep legacy tabs but redirect.
      orgs: { outer: 'agencies' },
      create: { outer: 'agencies' },
      agencies: { outer: 'agencies' },
      staff: { outer: 'staff' },
      notifications: { outer: 'notifications' },
      audit: { outer: 'audit' },
      analytics: { outer: 'analytics' },
      settings: { outer: 'settings' },
      pricing: { outer: 'settings', inner: 'pricing' },
      smtp: { outer: 'settings', inner: 'smtp' },
      emails: { outer: 'settings', inner: 'emails' },
      impressum: { outer: 'settings', inner: 'impressum' },
      datenschutz: { outer: 'settings', inner: 'datenschutz' },
      metaapp: { outer: 'settings', inner: 'metaapp' },
      licenses: { outer: 'agencies' },
    };
    const m = map[t] || { outer: 'analytics' };
    setOuterTab(m.outer);
    if (m.inner) setSettingsTab(m.inner);
  }, [location.search]);

  const [globalOverview, setGlobalOverview] = useState(null);
  const [globalSeries, setGlobalSeries] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const globalDays = 30;
  const [analyticsAgencyId, setAnalyticsAgencyId] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [rotatedLicense, setRotatedLicense] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [createdKey, setCreatedKey] = useState("");
  const [plainLicense, setPlainLicense] = useState("");
  // SMTP identities
  const [smtpItems, setSmtpItems] = useState([]);
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpCreating, setSmtpCreating] = useState(false);
  const [smtpForm, setSmtpForm] = useState({
    companyId: "",
    host: "",
    port: 587,
    secure: false,
    user: "",
    pass: "",
    fromEmail: "",
    fromName: "",
    enabled: true,
  });
  const [metaAppConfig, setMetaAppConfig] = useState({ appId: "", hasSecret: false });
  const [metaAppLoading, setMetaAppLoading] = useState(false);
  const [metaAppSaving, setMetaAppSaving] = useState(false);
  const [metaAppSecretInput, setMetaAppSecretInput] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invites, setInvites] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agencyLoading, setAgencyLoading] = useState(false);
  const [agencyForm, setAgencyForm] = useState({ name: "", billingEmail: "", ownerEmail: "" });
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [agencyDetailTab, setAgencyDetailTab] = useState('settings');
  const [agencyListPage, setAgencyListPage] = useState(1);
  const [agencySettingsForm, setAgencySettingsForm] = useState({
    name: "",
    billingEmail: "",
    status: "ACTIVE",
    ownerInviteEmail: "",
  });
  const [agencySettingsSaving, setAgencySettingsSaving] = useState(false);
  const [agencyOwnerInviting, setAgencyOwnerInviting] = useState(false);
  const [agencyBillingLoading, setAgencyBillingLoading] = useState(false);
  const [agencyInvoiceUploads, setAgencyInvoiceUploads] = useState([]);
  const [agencyInvoiceUploadsAgencyId, setAgencyInvoiceUploadsAgencyId] = useState(null);
  const [agencyInvoiceMonth, setAgencyInvoiceMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [agencyInvoiceFile, setAgencyInvoiceFile] = useState(null);
  const [agencyInvoiceUploading, setAgencyInvoiceUploading] = useState(false);
  const [agencyPricingLoading, setAgencyPricingLoading] = useState(false);
  const [agencyPricingSaving, setAgencyPricingSaving] = useState(false);
  const [agencyPricingSource, setAgencyPricingSource] = useState("default"); // default | global | agency
  const [agencyPricingRaw, setAgencyPricingRaw] = useState(null);
  const [agencyPricingGlobalRaw, setAgencyPricingGlobalRaw] = useState(null);
  const [agencyPricingAgencyId, setAgencyPricingAgencyId] = useState(null);
  const [agencyPricingForm, setAgencyPricingForm] = useState({
    currency: "EUR",
    baseFeeEuro: "0",
    perActiveOrgEuro: "0",
    per1000EmailsEuro: "0",
    perScanEuro: "0",
    perRedeemEuro: "0",
  });

  const [globalPricingDialog, setGlobalPricingDialog] = useState({ open: false });
  const [globalPricingLoading, setGlobalPricingLoading] = useState(false);
  const [globalPricingSaving, setGlobalPricingSaving] = useState(false);
  const [globalPricingRaw, setGlobalPricingRaw] = useState(null);
  const [globalPricingForm, setGlobalPricingForm] = useState({
    currency: "EUR",
    baseFeeEuro: "0",
    perActiveOrgEuro: "0",
    per1000EmailsEuro: "0",
    perScanEuro: "0",
    perRedeemEuro: "0",
  });

  const milliToEuroInput = (milli) => {
    const m = Number(milli) || 0;
    const sign = m < 0 ? "-" : "";
    const abs = Math.abs(m);
    const euros = Math.floor(abs / 1000);
    const frac = abs % 1000;
    if (frac === 0) return `${sign}${euros}`;
    const fracStr = String(frac).padStart(3, "0").replace(/0+$/, "");
    return `${sign}${euros}.${fracStr}`;
  };

  const euroInputToMilli = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return { ok: true, milli: 0 };
    const norm = raw.replace(/\s/g, "").replace(/,/g, ".");
    if (!/^[0-9]+(\.[0-9]{0,3})?$/.test(norm)) return { ok: false, milli: 0 };
    const [intPart, fracPartRaw] = norm.split(".");
    const fracPart = (fracPartRaw || "").padEnd(3, "0").slice(0, 3);
    const euros = Number(intPart || 0);
    const frac = Number(fracPart || 0);
    if (!Number.isFinite(euros) || !Number.isFinite(frac)) return { ok: false, milli: 0 };
    return { ok: true, milli: euros * 1000 + frac };
  };

  const parsePricingJson = (value) => {
    const fallback = {
      currency: "EUR",
      baseFeeEuro: "0",
      perActiveOrgEuro: "0",
      per1000EmailsEuro: "0",
      perScanEuro: "0",
      perRedeemEuro: "0",
    };
    if (!value) return { ok: false, pricing: fallback };
    try {
      const parsed = JSON.parse(String(value));
      const toIntSafe = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return 0;
        return Math.max(0, Math.trunc(n));
      };

      const hasMilli =
        parsed?.baseFeeMilli != null ||
        parsed?.perActiveOrgMilli != null ||
        parsed?.per1000EmailsMilli != null ||
        parsed?.perScanMilli != null ||
        parsed?.perRedeemMilli != null;

      const milli = {
        baseFeeMilli: hasMilli ? toIntSafe(parsed?.baseFeeMilli) : toIntSafe(parsed?.baseFeeCents) * 10,
        perActiveOrgMilli: hasMilli ? toIntSafe(parsed?.perActiveOrgMilli) : toIntSafe(parsed?.perActiveOrgCents) * 10,
        per1000EmailsMilli: hasMilli ? toIntSafe(parsed?.per1000EmailsMilli) : toIntSafe(parsed?.per1000EmailsCents) * 10,
        perScanMilli: hasMilli ? toIntSafe(parsed?.perScanMilli) : toIntSafe(parsed?.perScanCents) * 10,
        perRedeemMilli: hasMilli ? toIntSafe(parsed?.perRedeemMilli) : toIntSafe(parsed?.perRedeemCents) * 10,
      };

      const p = {
        currency: typeof parsed?.currency === "string" && parsed.currency ? parsed.currency : "EUR",
        baseFeeEuro: milliToEuroInput(milli.baseFeeMilli),
        perActiveOrgEuro: milliToEuroInput(milli.perActiveOrgMilli),
        per1000EmailsEuro: milliToEuroInput(milli.per1000EmailsMilli),
        perScanEuro: milliToEuroInput(milli.perScanMilli),
        perRedeemEuro: milliToEuroInput(milli.perRedeemMilli),
      };
      return { ok: true, pricing: p };
    } catch (_) {
      return { ok: false, pricing: fallback };
    }
  };

  const loadAgencyPricingFor = async (agencyId) => {
    if (!token || !agencyId) return;
    try {
      setAgencyPricingLoading(true);
      setAgencyPricingAgencyId(agencyId);
      const keyAgency = `agency_pricing:${agencyId}`;
      const [agencyResp, globalResp] = await Promise.all([
        api.systemSettingGet(token, keyAgency),
        api.systemSettingGet(token, "agency_pricing"),
      ]);

      const agencyValue = agencyResp?.value ?? null;
      const globalValue = globalResp?.value ?? null;
      setAgencyPricingRaw(agencyValue);
      setAgencyPricingGlobalRaw(globalValue);

      const parsedAgency = parsePricingJson(agencyValue);
      const parsedGlobal = parsePricingJson(globalValue);

      if (agencyValue) {
        setAgencyPricingSource("agency");
        setAgencyPricingForm(parsedAgency.pricing);
      } else if (globalValue) {
        setAgencyPricingSource("global");
        setAgencyPricingForm(parsedGlobal.pricing);
      } else {
        setAgencyPricingSource("default");
        setAgencyPricingForm(parsedGlobal.pricing);
      }
    } catch (e) {
      toast({ title: t('system.common.errorTitle'), description: e.message, variant: "destructive" });
    } finally {
      setAgencyPricingLoading(false);
    }
  };

  const saveAgencyPricingFor = async (agencyId) => {
    if (!token || !agencyId) return;
    const parsed = {
      baseFee: euroInputToMilli(agencyPricingForm.baseFeeEuro),
      perActiveOrg: euroInputToMilli(agencyPricingForm.perActiveOrgEuro),
      per1000Emails: euroInputToMilli(agencyPricingForm.per1000EmailsEuro),
      perScan: euroInputToMilli(agencyPricingForm.perScanEuro),
      perRedeem: euroInputToMilli(agencyPricingForm.perRedeemEuro),
    };
    if (!parsed.baseFee.ok || !parsed.perActiveOrg.ok || !parsed.per1000Emails.ok || !parsed.perScan.ok || !parsed.perRedeem.ok) {
      toast({
        title: t('system.common.errorTitle'),
        description: t('system.agencies.pricing.invalidEuro'),
        variant: 'destructive',
      });
      return;
    }
    try {
      setAgencyPricingSaving(true);
      const keyAgency = `agency_pricing:${agencyId}`;
      const payload = {
        currency: String(agencyPricingForm.currency || "EUR").trim() || "EUR",
        baseFeeMilli: parsed.baseFee.milli,
        perActiveOrgMilli: parsed.perActiveOrg.milli,
        per1000EmailsMilli: parsed.per1000Emails.milli,
        perScanMilli: parsed.perScan.milli,
        perRedeemMilli: parsed.perRedeem.milli,
      };
      await api.systemSettingSet(token, keyAgency, JSON.stringify(payload));
      toast({ title: t('system.common.saved') });
      setAgencyPricingRaw(JSON.stringify(payload));
      setAgencyPricingSource("agency");
    } catch (e) {
      toast({ title: t('system.common.errorTitle'), description: e.message, variant: "destructive" });
    } finally {
      setAgencyPricingSaving(false);
    }
  };

  const resetAgencyPricingToGlobalFor = async (agencyId) => {
    if (!token || !agencyId) return;
    try {
      setAgencyPricingSaving(true);
      const keyAgency = `agency_pricing:${agencyId}`;
      await api.systemSettingSet(token, keyAgency, null);
      toast({ title: t('system.common.saved') });
      setAgencyPricingRaw(null);

      const parsedGlobal = parsePricingJson(agencyPricingGlobalRaw);
      if (agencyPricingGlobalRaw) {
        setAgencyPricingSource("global");
        setAgencyPricingForm(parsedGlobal.pricing);
      } else {
        setAgencyPricingSource("default");
        setAgencyPricingForm(parsedGlobal.pricing);
      }
    } catch (e) {
      toast({ title: t('system.common.errorTitle'), description: e.message, variant: "destructive" });
    } finally {
      setAgencyPricingSaving(false);
    }
  };

  const openGlobalPricing = async () => {
    setGlobalPricingDialog({ open: true });
    if (!token) return;
    try {
      setGlobalPricingLoading(true);
      const resp = await api.systemSettingGet(token, "agency_pricing");
      const value = resp?.value ?? null;
      setGlobalPricingRaw(value);
      const parsed = parsePricingJson(value);
      setGlobalPricingForm(parsed.pricing);
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setGlobalPricingLoading(false);
    }
  };

  const formatMoney = (amountCents, currency) => {
    if (amountCents == null) return "—";
    const n = Number(amountCents) / 100;
    try {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: currency || "EUR",
      }).format(n);
    } catch (_) {
      return `${n.toFixed(2)} ${currency || "EUR"}`;
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'invoice.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const refreshAgencyBillingUploadsFor = async (agencyId) => {
    if (!token || !agencyId) return;
    try {
      setAgencyBillingLoading(true);
      const uploads = await api.systemAgencyInvoiceUploads(token, agencyId);
      setAgencyInvoiceUploads(uploads?.items || []);
      setAgencyInvoiceUploadsAgencyId(agencyId);
    } catch (_) {
    } finally {
      setAgencyBillingLoading(false);
    }
  };

  const uploadAgencyInvoiceFor = async (agencyId) => {
    if (!token || !agencyId) return;
    if (!agencyInvoiceMonth || !/^[0-9]{4}-[0-9]{2}$/.test(agencyInvoiceMonth)) {
      toast({ title: t('system.common.errorTitle'), description: t('system.agencies.billing.badMonth'), variant: 'destructive' });
      return;
    }
    if (!agencyInvoiceFile) {
      toast({ title: t('system.common.errorTitle'), description: t('system.agencies.billing.fileRequired'), variant: 'destructive' });
      return;
    }
    try {
      setAgencyInvoiceUploading(true);
      await api.systemAgencyInvoiceUpload(token, agencyId, agencyInvoiceMonth, agencyInvoiceFile);
      toast({ title: t('system.common.saved') });
      setAgencyInvoiceFile(null);
      await refreshAgencyBillingUploadsFor(agencyId);
    } catch (e) {
      toast({ title: t('system.common.errorTitle'), description: e.message, variant: 'destructive' });
    } finally {
      setAgencyInvoiceUploading(false);
    }
  };

  const selectedAgency = selectedAgencyId ? agencies.find((a) => a.id === selectedAgencyId) : null;
  const AGENCY_PAGE_SIZE = 10;
  const agencyTotalPages = Math.max(1, Math.ceil((agencies?.length || 0) / AGENCY_PAGE_SIZE));
  const agencyPageSafe = Math.min(Math.max(1, agencyListPage), agencyTotalPages);
  const agencyPageItems = (agencies || []).slice((agencyPageSafe - 1) * AGENCY_PAGE_SIZE, agencyPageSafe * AGENCY_PAGE_SIZE);

  useEffect(() => {
    if (agencyListPage !== agencyPageSafe) setAgencyListPage(agencyPageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyTotalPages]);

  useEffect(() => {
    if (!selectedAgency) return;
    setAgencySettingsForm({
      name: selectedAgency.name || "",
      billingEmail: selectedAgency.billingEmail || "",
      status: selectedAgency.status || "ACTIVE",
      ownerInviteEmail: selectedAgency.ownerInviteEmail || "",
    });
  }, [selectedAgencyId, agencies]);

  useEffect(() => {
    if (!selectedAgencyId) return;
    if (agencyDetailTab === 'pdfs' && agencyInvoiceUploadsAgencyId !== selectedAgencyId) {
      refreshAgencyBillingUploadsFor(selectedAgencyId);
    }
    if (agencyDetailTab === 'pricing' && agencyPricingAgencyId !== selectedAgencyId) {
      loadAgencyPricingFor(selectedAgencyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyDetailTab, selectedAgencyId]);

  const openAgencyDetail = (agencyId) => {
    setSelectedAgencyId(agencyId);
    setAgencyDetailTab('settings');
    setAgencyInvoiceFile(null);
  };

  const backToAgencyList = () => {
    setSelectedAgencyId(null);
    setAgencyDetailTab('settings');
    setAgencyInvoiceFile(null);
  };

  const saveAgencySettings = async () => {
    if (!token || !selectedAgencyId) return;
    if (!agencySettingsForm.name.trim()) {
      toast({ title: t('system.common.errorTitle'), description: t('system.agencies.errors.nameRequired'), variant: 'destructive' });
      return;
    }
    try {
      setAgencySettingsSaving(true);
      await api.updateAgency(token, selectedAgencyId, {
        name: agencySettingsForm.name.trim(),
        billingEmail: agencySettingsForm.billingEmail.trim() || null,
        status: agencySettingsForm.status || 'ACTIVE',
      });
      toast({ title: t('system.common.saved') });
      await loadAgencies();
    } catch (e) {
      toast({ title: t('system.common.errorTitle'), description: e.message, variant: 'destructive' });
    } finally {
      setAgencySettingsSaving(false);
    }
  };

  const inviteAgencyOwnerForSelected = async () => {
    if (!token || !selectedAgencyId) return;
    const email = String(agencySettingsForm.ownerInviteEmail || '').trim();
    if (!email) return;
    try {
      setAgencyOwnerInviting(true);
      const resp = await api.inviteAgencyOwner(token, selectedAgencyId, email);
      if (resp?.inviteUrl) {
        toast({ title: t('system.agencies.inviteOwnerSuccess'), description: `Invite-Link: ${resp.inviteUrl}` });
      } else {
        toast({ title: t('system.agencies.inviteOwnerSuccess') });
      }
      await loadAgencies();
    } catch (e) {
      toast({ title: t('system.common.errorTitle'), description: e.message, variant: 'destructive' });
    } finally {
      setAgencyOwnerInviting(false);
    }
  };

  const saveGlobalPricing = async () => {
    if (!token) return;
    const parsed = {
      baseFee: euroInputToMilli(globalPricingForm.baseFeeEuro),
      perActiveOrg: euroInputToMilli(globalPricingForm.perActiveOrgEuro),
      per1000Emails: euroInputToMilli(globalPricingForm.per1000EmailsEuro),
      perScan: euroInputToMilli(globalPricingForm.perScanEuro),
      perRedeem: euroInputToMilli(globalPricingForm.perRedeemEuro),
    };
    if (!parsed.baseFee.ok || !parsed.perActiveOrg.ok || !parsed.per1000Emails.ok || !parsed.perScan.ok || !parsed.perRedeem.ok) {
      toast({
        title: t('system.common.errorTitle'),
        description: t('system.agencies.pricing.invalidEuro'),
        variant: 'destructive',
      });
      return;
    }
    try {
      setGlobalPricingSaving(true);
      const payload = {
        currency: String(globalPricingForm.currency || "EUR").trim() || "EUR",
        baseFeeMilli: parsed.baseFee.milli,
        perActiveOrgMilli: parsed.perActiveOrg.milli,
        per1000EmailsMilli: parsed.per1000Emails.milli,
        perScanMilli: parsed.perScan.milli,
        perRedeemMilli: parsed.perRedeem.milli,
      };
      await api.systemSettingSet(token, "agency_pricing", JSON.stringify(payload));
      toast({ title: t('system.common.saved') });
      setGlobalPricingRaw(JSON.stringify(payload));
      setAgencyPricingGlobalRaw(JSON.stringify(payload));
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setGlobalPricingSaving(false);
    }
  };

  const resetGlobalPricingToDefault = async () => {
    if (!token) return;
    try {
      setGlobalPricingSaving(true);
      await api.systemSettingSet(token, "agency_pricing", null);
      toast({ title: t('system.common.saved') });
      setGlobalPricingRaw(null);
      setAgencyPricingGlobalRaw(null);
      const parsed = parsePricingJson(null);
      setGlobalPricingForm(parsed.pricing);
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setGlobalPricingSaving(false);
    }
  };

  const loadOrgs = async () => {
    if (!token) return;
    try {
      const items = await api.listOrganizations(token);
      setOrgs(items);
    } catch (e) {
      toast({
        title: t('system.common.loadErrorTitle'),
        description: e.message,
        variant: "destructive",
      });
    }
  };
  // Org management is no longer shown in system dashboard

  const loadAgencies = async () => {
    if (!token) return;
    try {
      setAgencyLoading(true);
      const items = await api.listAgencies(token);
      setAgencies(items || []);
    } catch (e) {
      toast({
        title: t('system.common.loadErrorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setAgencyLoading(false);
    }
  };

  useEffect(() => {
    loadAgencies();
  }, [token]);

  const loadGlobalDashboard = async () => {
    if (!token) return;
    try {
      setGlobalLoading(true);
      const agencyScope = analyticsAgencyId ? { agencyId: analyticsAgencyId } : undefined;
      const [o, ts] = await Promise.all([
        api.analyticsGlobalOverview(token, agencyScope),
        api.analyticsGlobalTimeseries(token, globalDays, agencyScope),
      ]);
      setGlobalOverview(o);
      setGlobalSeries(ts);
    } catch (e) {
      setGlobalOverview(null);
      setGlobalSeries(null);
      toast({ title: t('system.common.loadErrorTitle'), description: e.message, variant: 'destructive' });
    } finally {
      setGlobalLoading(false);
    }
  };

  useEffect(() => {
    loadGlobalDashboard();
  }, [token, analyticsAgencyId]);

  const loadSmtp = async () => {
    if (!token) return;
    try {
      setSmtpLoading(true);
      const res = await api.smtpList(token);
      setSmtpItems(res.items || []);
    } catch (e) {
      toast({ title: t('system.common.errorTitle'), description: e.message, variant: "destructive" });
    } finally {
      setSmtpLoading(false);
    }
  };

  useEffect(() => {
    loadSmtp();
  }, [token]);

  useEffect(() => {
    loadMetaAppConfig();
  }, [token]);

  const loadMetaAppConfig = async () => {
    if (!token) return;
    try {
      setMetaAppLoading(true);
      const res = await api.metaAppConfigGet(token);
      setMetaAppConfig({
        appId: res?.appId || "",
        hasSecret: !!res?.hasSecret,
      });
      setMetaAppSecretInput("");
    } catch (e) {
      toast({ title: t('system.common.errorTitle'), description: e.message, variant: "destructive" });
    } finally {
      setMetaAppLoading(false);
    }
  };

  const createOrg = async (e) => {
    e.preventDefault();
    if (!name.trim() || !ownerEmail.trim()) { toast({ title: t('system.common.errorTitle'), description: t('system.org.errors.missingNameOrEmail'), variant: 'destructive' }); return; }
    try {
      setCreating(true);
      const res = await api.createOrganization(token, name.trim(), ownerEmail.trim());
      toast({ title: t('system.org.createSuccess') });
      setName("");
      if (res?.licenseKey) {
        setCreatedKey(res.licenseKey);
        toast({ title: t('system.org.license.generated'), description: t('system.org.license.copyNow') });
      }
  // send owner invite
      try {
        await api.inviteOwner(token, res.id, ownerEmail.trim());
        toast({ title: t('system.owner.inviteSent'), description: ownerEmail });
      } catch (e) {
        toast({ title: t('system.common.errorTitle'), description: e.message, variant: 'destructive' });
      }
      setOwnerEmail("");
      await loadOrgs();
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const loadInvites = async (orgId) => {
    try {
      const res = await api.listInvites(token, orgId);
      setInvites(res.items || []);
    } catch (_) {}
  };

  const openOrg = async (id) => {
    setActiveOrgId(id);
    setOrgDetail(null);
    setRotatedLicense(null);
    setResetResult(null);
    try {
      const detail = await api.organizationDetail(token, id);
      setOrgDetail(detail);
      await loadInvites(id);
      setPlainLicense("");
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    }
  };

  const saveOrgBasics = async (e) => {
    e.preventDefault();
    if (!orgDetail) return;
    try {
      setUpdating(true);
      await api.updateOrganization(token, orgDetail.id, {
        name: orgDetail.name,
        email: orgDetail.email,
        sendmireinEnabled: orgDetail.sendmireinEnabled,
      });
      toast({ title: t('system.common.saved') });
      await loadOrgs();
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const changeStatus = async (status) => {
    if (!orgDetail) return;
    try {
      await api.setOrganizationStatus(token, orgDetail.id, status);
      setOrgDetail({ ...orgDetail, status });
      toast({ title: t('system.org.statusUpdated') });
      await loadOrgs();
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    }
  };

  const requestPasswordReset = async () => {
    if (!orgDetail) return;
    try {
      const res = await api.requestPasswordReset(token, orgDetail.id);
      setResetResult(res);
      toast({ 
        title: t('system.owner.resetLinkSent'), 
        description: t('system.owner.resetEmailSent', { email: res.email })
      });
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    }
  };

  const rotateLicense = async () => {
    if (!orgDetail) return;
    try {
      const res = await api.rotateLicense(token, orgDetail.id);
      const key = res?.licenseKey || res?.plaintext || res?.key;
      if (key) {
        setRotatedLicense(key);
        toast({ title: t('system.org.license.new'), description: t('system.org.license.copyNow') });
      }
      const detail = await api.organizationDetail(token, orgDetail.id);
      setOrgDetail(detail);
    } catch (e) {
      toast({
        title: t('system.common.errorTitle'),
        description: e.message,
        variant: "destructive",
      });
    }
  };

  const overview = globalOverview || {
    companies: 0,
    users: 0,
    campaigns: 0,
    actions: 0,
    scans: 0,
    redeems: 0,
  };

  return (
    <div className="min-h-screen bg-muted">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
          <StatCard
            icon={Building2}
            title={t('system.stats.organizations')}
            value={overview.companies}
          />
          <StatCard icon={Users} title={t('system.stats.users')} value={overview.users} />
          <StatCard
            icon={Activity}
            title={t('system.stats.campaigns')}
            value={overview.campaigns}
          />
          <StatCard icon={Cpu} title={t('system.stats.actions')} value={overview.actions} />
          <StatCard icon={BarChart3} title={t('system.stats.scans')} value={overview.scans} />
          <StatCard
            icon={Archive}
            title={t('system.stats.redeems')}
            value={overview.redeems}
          />
        </div>
        <Tabs value={outerTab} onValueChange={(v)=>{
          setOuterTab(v);
          // reflect selection in URL (flat tabs)
          if (v === 'settings') {
            navigate(`/system?tab=${settingsTab}`);
          } else {
            navigate(`/system?tab=${v}`);
          }
        }} className="space-y-6">
          {/* Tab list removed; navigation happens via sidebar links updating ?tab= */}
          {/* Organization management removed from System Dashboard */}

          <TabsContent value="agencies">
            <div className="flex flex-col lg:flex-row gap-4">
              <Card className="lg:w-80 lg:shrink-0">
                <CardHeader>
                  <CardTitle>{t('system.agencies.listTitle')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {agencyLoading && <div className="text-muted-foreground text-sm">{t('common.loading')}</div>}
                  {!agencyLoading && agencies.length === 0 && <div className="text-muted-foreground text-sm">{t('system.agencies.none')}</div>}
                  {!agencyLoading && agencies.length > 0 && (
                    <div className="space-y-1">
                      {agencyPageItems.map((a) => {
                        const active = a.id === selectedAgencyId;
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => openAgencyDetail(a.id)}
                            className={`w-full text-left border rounded px-3 py-2 hover:bg-muted ${active ? 'bg-muted border-border' : ''}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-medium truncate">{a.name}</div>
                              <span className="text-[11px] px-2 py-0.5 rounded bg-muted text-foreground">{a.status}</span>
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate">{a.billingEmail || '-'}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {!agencyLoading && agencies.length > 0 && (
                    <div className="flex items-center justify-between pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        type="button"
                        disabled={agencyPageSafe <= 1}
                        onClick={() => setAgencyListPage((p) => Math.max(1, p - 1))}
                      >
                        {t('common.prev')}
                      </Button>
                      <div className="text-xs text-muted-foreground">
                        {agencyPageSafe} / {agencyTotalPages}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        type="button"
                        disabled={agencyPageSafe >= agencyTotalPages}
                        onClick={() => setAgencyListPage((p) => Math.min(agencyTotalPages, p + 1))}
                      >
                        {t('common.next')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex-1 space-y-4">
                {selectedAgency ? (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between gap-2">
                        <CardTitle>{selectedAgency.name}</CardTitle>
                        <Button variant="outline" size="sm" type="button" onClick={backToAgencyList}>
                          {t('common.back')}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Tabs value={agencyDetailTab} onValueChange={setAgencyDetailTab} className="space-y-4">
                        <TabsList className="grid w-full grid-cols-3">
                          <TabsTrigger value="settings">{t('system.agencies.detail.tabs.settings')}</TabsTrigger>
                          <TabsTrigger value="pdfs">
                            {t('system.agencies.detail.tabs.pdfs')}
                          </TabsTrigger>
                          <TabsTrigger value="pricing">
                            {t('system.agencies.detail.tabs.pricing')}
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="settings">
                          <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <div className="text-[10px] text-muted-foreground">{t('common.name')}</div>
                                <input
                                  className="w-full border rounded px-2 py-1"
                                  value={agencySettingsForm.name}
                                  onChange={(e) => setAgencySettingsForm((p) => ({ ...p, name: e.target.value }))}
                                />
                              </div>

                              <div>
                                <div className="text-[10px] text-muted-foreground">{t('system.agencies.form.billingEmail')}</div>
                                <input
                                  className="w-full border rounded px-2 py-1"
                                  value={agencySettingsForm.billingEmail}
                                  onChange={(e) => setAgencySettingsForm((p) => ({ ...p, billingEmail: e.target.value }))}
                                  placeholder="abrechnung@example.com"
                                />
                              </div>

                              <div>
                                <div className="text-[10px] text-muted-foreground">{t('common.status')}</div>
                                <select
                                  className="w-full border rounded px-2 py-1"
                                  value={agencySettingsForm.status}
                                  onChange={(e) => setAgencySettingsForm((p) => ({ ...p, status: e.target.value }))}
                                >
                                  <option value="ACTIVE">ACTIVE</option>
                                  <option value="INACTIVE">INACTIVE</option>
                                </select>
                              </div>

                              <div>
                                <div className="text-[10px] text-muted-foreground">{t('system.agencies.inviteOwner')}</div>
                                <div className="flex gap-2">
                                  <input
                                    className="w-full border rounded px-2 py-1"
                                    type="email"
                                    value={agencySettingsForm.ownerInviteEmail}
                                    onChange={(e) => setAgencySettingsForm((p) => ({ ...p, ownerInviteEmail: e.target.value }))}
                                    placeholder="owner@example.com"
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    type="button"
                                    disabled={agencyOwnerInviting || !String(agencySettingsForm.ownerInviteEmail || '').trim()}
                                    onClick={inviteAgencyOwnerForSelected}
                                  >
                                    {agencyOwnerInviting ? "..." : t('system.agencies.inviteOwner')}
                                  </Button>
                                </div>
                                {selectedAgency.ownerInviteEmail && (
                                  <div className="text-[11px] text-blue-700 mt-1">
                                    {t('system.agencies.ownerInviteInfo', {
                                      email: selectedAgency.ownerInviteEmail,
                                      date: new Date(selectedAgency.ownerInviteExpiresAt).toLocaleDateString('de-DE'),
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex justify-end pt-2">
                              <Button size="sm" type="button" disabled={agencySettingsSaving} onClick={saveAgencySettings}>
                                {agencySettingsSaving ? "..." : t('common.save')}
                              </Button>
                            </div>
                          </div>
                        </TabsContent>

                        <TabsContent value="pdfs">
                          <div className="space-y-4 text-sm">
                            <div className="space-y-2">
                              <div className="text-xs text-muted-foreground">{t('system.agencies.billing.uploadTitle')}</div>
                              <div className="flex flex-col md:flex-row gap-2 items-start md:items-end">
                                <div className="flex-1">
                                  <label className="text-xs text-muted-foreground">{t('system.agencies.billing.monthLabel')}</label>
                                  <input
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    value={agencyInvoiceMonth}
                                    onChange={(e) => setAgencyInvoiceMonth(e.target.value)}
                                    placeholder="YYYY-MM"
                                  />
                                </div>
                                <div className="flex-1">
                                  <label className="text-xs text-muted-foreground">{t('system.agencies.billing.fileLabel')}</label>
                                  <input
                                    type="file"
                                    accept="application/pdf"
                                    className="w-full text-sm"
                                    onChange={(e) => setAgencyInvoiceFile(e.target.files?.[0] || null)}
                                  />
                                </div>
                                <div>
                                  <Button
                                    size="sm"
                                    type="button"
                                    disabled={agencyInvoiceUploading || agencyBillingLoading}
                                    onClick={() => uploadAgencyInvoiceFor(selectedAgencyId)}
                                  >
                                    {agencyInvoiceUploading ? "..." : t('system.agencies.billing.upload')}
                                  </Button>
                                </div>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <div className="text-xs text-muted-foreground">{t('system.agencies.billing.uploadsTitle')}</div>
                              {agencyBillingLoading ? (
                                <div className="text-xs text-muted-foreground">{t('common.loading')}</div>
                              ) : !agencyInvoiceUploads?.length ? (
                                <div className="text-xs text-muted-foreground">{t('system.agencies.billing.noUploads')}</div>
                              ) : (
                                <div className="space-y-2">
                                  {agencyInvoiceUploads.map((u) => (
                                    <div key={u.id} className="border rounded p-2 flex items-center justify-between gap-2">
                                      <div className="min-w-0">
                                        <div className="text-sm font-medium">{u.label}</div>
                                        <div className="text-[11px] text-muted-foreground truncate">{u.fileName}</div>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        type="button"
                                        onClick={async () => {
                                          try {
                                            const { blob, filename } = await api.systemAgencyInvoiceDownload(
                                              token,
                                              selectedAgencyId,
                                              u.id
                                            );
                                            downloadBlob(blob, filename || u.fileName);
                                          } catch (e) {
                                            toast({ title: t('system.common.errorTitle'), description: e.message, variant: 'destructive' });
                                          }
                                        }}
                                      >
                                        {t('system.agencies.billing.download')}
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </TabsContent>

                        <TabsContent value="pricing">
                          {agencyPricingLoading ? (
                            <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
                          ) : (
                            <div className="space-y-3 text-sm">
                              <div className="text-xs text-muted-foreground">
                                {agencyPricingSource === 'agency'
                                  ? t('system.agencies.pricing.sourceAgency')
                                  : agencyPricingSource === 'global'
                                  ? t('system.agencies.pricing.sourceGlobal')
                                  : t('system.agencies.pricing.sourceDefault')}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.currency')}</label>
                                  <input
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    value={agencyPricingForm.currency}
                                    onChange={(e) => setAgencyPricingForm((p) => ({ ...p, currency: e.target.value }))}
                                    placeholder="EUR"
                                  />
                                </div>

                                <div>
                                  <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.baseFeeCents')}</label>
                                  <input
                                    inputMode="decimal"
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    value={agencyPricingForm.baseFeeEuro}
                                    onChange={(e) => setAgencyPricingForm((p) => ({ ...p, baseFeeEuro: e.target.value }))}
                                  />
                                </div>

                                <div>
                                  <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.perActiveOrgCents')}</label>
                                  <input
                                    inputMode="decimal"
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    value={agencyPricingForm.perActiveOrgEuro}
                                    onChange={(e) => setAgencyPricingForm((p) => ({ ...p, perActiveOrgEuro: e.target.value }))}
                                  />
                                </div>

                                <div>
                                  <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.per1000EmailsCents')}</label>
                                  <input
                                    inputMode="decimal"
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    value={agencyPricingForm.per1000EmailsEuro}
                                    onChange={(e) => setAgencyPricingForm((p) => ({ ...p, per1000EmailsEuro: e.target.value }))}
                                  />
                                </div>

                                <div>
                                  <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.perScanCents')}</label>
                                  <input
                                    inputMode="decimal"
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    value={agencyPricingForm.perScanEuro}
                                    onChange={(e) => setAgencyPricingForm((p) => ({ ...p, perScanEuro: e.target.value }))}
                                  />
                                </div>

                                <div>
                                  <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.perRedeemCents')}</label>
                                  <input
                                    inputMode="decimal"
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    value={agencyPricingForm.perRedeemEuro}
                                    onChange={(e) => setAgencyPricingForm((p) => ({ ...p, perRedeemEuro: e.target.value }))}
                                  />
                                </div>
                              </div>

                              <div className="flex justify-between items-center pt-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  type="button"
                                  disabled={agencyPricingSaving}
                                  onClick={() => resetAgencyPricingToGlobalFor(selectedAgencyId)}
                                >
                                  {t('system.agencies.pricing.resetToGlobal')}
                                </Button>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    type="button"
                                    disabled={agencyPricingSaving}
                                    onClick={() => saveAgencyPricingFor(selectedAgencyId)}
                                  >
                                    {agencyPricingSaving ? "..." : t('common.save')}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('system.agencies.detail.createTitle')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form
                        className="space-y-3 text-sm"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          if (!agencyForm.name.trim()) {
                            toast({
                              title: t('system.common.errorTitle'),
                              description: t('system.agencies.errors.nameRequired'),
                              variant: 'destructive',
                            });
                            return;
                          }
                          try {
                            const created = await api.createAgency(token, {
                              name: agencyForm.name.trim(),
                              billingEmail: agencyForm.billingEmail.trim() || undefined,
                              ownerEmail: agencyForm.ownerEmail.trim() || undefined,
                            });
                            if (created?.ownerInvite?.url) {
                              toast({ title: t('system.common.saved'), description: `Invite-Link: ${created.ownerInvite.url}` });
                            } else {
                              toast({ title: t('system.common.saved') });
                            }
                            setAgencyForm({ name: "", billingEmail: "", ownerEmail: "" });
                            await loadAgencies();
                          } catch (err) {
                            toast({ title: t('system.common.errorTitle'), description: err.message, variant: 'destructive' });
                          }
                        }}
                      >
                        <div>
                          <div className="text-[10px] text-muted-foreground">{t('system.agencies.form.name')}</div>
                          <input
                            className="w-full border rounded px-2 py-1"
                            value={agencyForm.name}
                            onChange={(e) => setAgencyForm({ ...agencyForm, name: e.target.value })}
                          />
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground">{t('system.agencies.form.billingEmail')}</div>
                          <input
                            className="w-full border rounded px-2 py-1"
                            value={agencyForm.billingEmail}
                            onChange={(e) => setAgencyForm({ ...agencyForm, billingEmail: e.target.value })}
                            placeholder="abrechnung@example.com"
                          />
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground">{t('system.agencies.form.ownerEmail')}</div>
                          <input
                            className="w-full border rounded px-2 py-1"
                            type="email"
                            value={agencyForm.ownerEmail}
                            onChange={(e) => setAgencyForm({ ...agencyForm, ownerEmail: e.target.value })}
                            placeholder="owner@example.com"
                          />
                        </div>
                        <div>
                          <Button type="submit" size="sm">{t('system.agencies.form.create')}</Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

              <Dialog
                open={globalPricingDialog.open}
                onOpenChange={(open) => {
                  setGlobalPricingDialog({ open });
                }}
              >
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{t('system.agencies.pricingGlobal.title')}</DialogTitle>
                  </DialogHeader>

                  {globalPricingLoading ? (
                    <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
                  ) : (
                    <div className="space-y-3 text-sm">
                      <div className="text-xs text-muted-foreground">
                        {globalPricingRaw ? t('system.agencies.pricingGlobal.sourceConfigured') : t('system.agencies.pricingGlobal.sourceDefault')}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.currency')}</label>
                          <input
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={globalPricingForm.currency}
                            onChange={(e) => setGlobalPricingForm((p) => ({ ...p, currency: e.target.value }))}
                            placeholder="EUR"
                          />
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.baseFeeCents')}</label>
                          <input
                            inputMode="decimal"
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={globalPricingForm.baseFeeEuro}
                            onChange={(e) => setGlobalPricingForm((p) => ({ ...p, baseFeeEuro: e.target.value }))}
                          />
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.perActiveOrgCents')}</label>
                          <input
                            inputMode="decimal"
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={globalPricingForm.perActiveOrgEuro}
                            onChange={(e) => setGlobalPricingForm((p) => ({ ...p, perActiveOrgEuro: e.target.value }))}
                          />
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.per1000EmailsCents')}</label>
                          <input
                            inputMode="decimal"
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={globalPricingForm.per1000EmailsEuro}
                            onChange={(e) => setGlobalPricingForm((p) => ({ ...p, per1000EmailsEuro: e.target.value }))}
                          />
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.perScanCents')}</label>
                          <input
                            inputMode="decimal"
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={globalPricingForm.perScanEuro}
                            onChange={(e) => setGlobalPricingForm((p) => ({ ...p, perScanEuro: e.target.value }))}
                          />
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground">{t('system.agencies.pricing.perRedeemCents')}</label>
                          <input
                            inputMode="decimal"
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={globalPricingForm.perRedeemEuro}
                            onChange={(e) => setGlobalPricingForm((p) => ({ ...p, perRedeemEuro: e.target.value }))}
                          />
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          disabled={globalPricingSaving}
                          onClick={resetGlobalPricingToDefault}
                        >
                          {t('system.agencies.pricingGlobal.resetToDefault')}
                        </Button>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            onClick={() => setGlobalPricingDialog({ open: false })}
                          >
                            {t('common.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            type="button"
                            disabled={globalPricingSaving}
                            onClick={saveGlobalPricing}
                          >
                            {globalPricingSaving ? "..." : t('common.save')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </TabsContent>

          <TabsContent value="analytics">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">{t('common.overview')}</div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{t('nav.agencies')}</span>
                    <select
                      className="border rounded px-2 py-1 text-sm bg-background"
                      value={analyticsAgencyId}
                      onChange={(e) => setAnalyticsAgencyId(e.target.value)}
                      disabled={agencyLoading}
                    >
                      <option value="">{t('common.all')}</option>
                      {agencies.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button size="sm" variant="outline" type="button" onClick={loadGlobalDashboard} disabled={globalLoading}>
                    {globalLoading ? "..." : t('common.reload')}
                  </Button>
                </div>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{t('agency.timeseries')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {globalLoading && !globalSeries ? (
                    <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
                  ) : globalSeries?.labels?.length ? (
                    <div className="h-64">
                      <TimeseriesChart
                        labels={globalSeries.labels}
                        scans={globalSeries.scans || []}
                        redeems={globalSeries.redeems || []}
                        onSelect={null}
                      />
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">{t('common.noData')}</div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>



          <TabsContent value="staff">
            <StaffManagement token={token} />
          </TabsContent>

          <TabsContent value="notifications">
            <div className="space-y-6">
              <NotificationsAdminSummary />
              <NotificationSender />
            </div>
          </TabsContent>

          <TabsContent value="audit">
            <AuditLogs token={token} />
          </TabsContent>
 
          <TabsContent value="settings">
            <div className="space-y-4">
              <Tabs value={settingsTab} onValueChange={(v)=>{ setSettingsTab(v); navigate(`/system?tab=${v}`); }} className="space-y-4">
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="impressum">Impressum</TabsTrigger>
                  <TabsTrigger value="datenschutz">Datenschutz</TabsTrigger>
                  <TabsTrigger value="pricing">Feature-Preise</TabsTrigger>
                  <TabsTrigger value="emails">{t('system.tabs.emails')}</TabsTrigger>
                  <TabsTrigger value="smtp">SMTP</TabsTrigger>
                  <TabsTrigger value="metaapp">{t('system.metaApp.title')}</TabsTrigger>
                </TabsList>

                <TabsContent value="impressum">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Footer & Rechtliches</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm">
                        <ImpressumEditor token={token} />
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="datenschutz">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Datenschutzerklärung</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm">
                        <DatenschutzEditor token={token} />
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="pricing">
                  <FeaturePricingManagement token={token} />
                </TabsContent>

                <TabsContent value="emails">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('system.tabs.emails')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4 text-sm">
                        <EmailTemplateEditor token={token} keyName="verify" label="Bestätigungs-Mail (verify)" />
                        <EmailTemplateEditor token={token} keyName="code" label="Code-Mail (code)" />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle>Testversand</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <EmailTemplateTester token={token} />
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="smtp">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>SMTP Identitäten</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={loadSmtp} disabled={smtpLoading}>
                            {smtpLoading ? "Lade..." : "Neu laden"}
                          </Button>
                        </div>
                        <div className="space-y-3">
                          {smtpItems.map((it) => (
                            <div key={it.id} className="border rounded p-2 space-y-2 text-xs">
                              <div className="flex items-center justify-between">
                                <div className="font-medium">{it.fromName || it.fromEmail}</div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded ${it.enabled ? 'bg-green-100 text-green-700' : 'bg-muted text-foreground'}`}>
                                    {it.enabled ? t('system.common.active') : t('system.common.inactive')}
                                  </span>
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={async () => {
                                      try {
                                        await api.smtpUpdate(token, it.id, { enabled: !it.enabled });
                                        toast({ title: it.enabled ? t('system.common.deactivated') : t('system.common.activated') });
                                        loadSmtp();
                                      } catch (e) {
                                        toast({ title: 'Update Fehler', description: e.message, variant: 'destructive' });
                                      }
                                    }}
                                  >
                                    {it.enabled ? 'Deaktivieren' : 'Aktivieren'}
                                  </Button>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <div className="text-[10px] text-muted-foreground">From Email</div>
                                  <input
                                    className="w-full border rounded px-2 py-1"
                                    defaultValue={it.fromEmail}
                                    onBlur={async (e) => {
                                      const v = e.target.value;
                                      if (v && v !== it.fromEmail) {
                                        try { await api.smtpUpdate(token, it.id, { fromEmail: v }); toast({ title: t('system.common.saved') }); loadSmtp(); } catch (err) { toast({ title: t('system.common.errorTitle'), description: err.message, variant: 'destructive' }); }
                                      }
                                    }}
                                  />
                                </div>
                                <div>
                                  <div className="text-[10px] text-muted-foreground">From Name</div>
                                  <input
                                    className="w-full border rounded px-2 py-1"
                                    defaultValue={it.fromName || ''}
                                    onBlur={async (e) => {
                                      const v = e.target.value;
                                      if (v !== (it.fromName || '')) {
                                        try { await api.smtpUpdate(token, it.id, { fromName: v || null }); toast({ title: t('system.common.saved') }); loadSmtp(); } catch (err) { toast({ title: t('system.common.errorTitle'), description: err.message, variant: 'destructive' }); }
                                      }
                                    }}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <div className="text-[10px] text-muted-foreground">Host</div>
                                  <input
                                    className="w-full border rounded px-2 py-1"
                                    defaultValue={it.host}
                                    onBlur={async (e) => {
                                      const v = e.target.value;
                                      if (v && v !== it.host) {
                                        try { await api.smtpUpdate(token, it.id, { host: v }); toast({ title: t('system.common.saved') }); loadSmtp(); } catch (err) { toast({ title: t('system.common.errorTitle'), description: err.message, variant: 'destructive' }); }
                                      }
                                    }}
                                  />
                                </div>
                                <div>
                                  <div className="text-[10px] text-muted-foreground">Port</div>
                                  <input
                                    type="number"
                                    className="w-full border rounded px-2 py-1"
                                    defaultValue={it.port || 587}
                                    onBlur={async (e) => {
                                      const v = parseInt(e.target.value || '587', 10);
                                      if (v !== (it.port || 587)) {
                                        try { await api.smtpUpdate(token, it.id, { port: v }); toast({ title: t('system.common.saved') }); loadSmtp(); } catch (err) { toast({ title: t('system.common.errorTitle'), description: err.message, variant: 'destructive' }); }
                                      }
                                    }}
                                  />
                                </div>
                                <div>
                                  <div className="text-[10px] text-muted-foreground">Secure</div>
                                  <select
                                    className="w-full border rounded px-2 py-1"
                                    defaultValue={it.secure ? 'true' : 'false'}
                                    onChange={async (e) => {
                                      const v = e.target.value === 'true';
                                      if (v !== !!it.secure) {
                                        try { await api.smtpUpdate(token, it.id, { secure: v }); toast({ title: t('system.common.saved') }); loadSmtp(); } catch (err) { toast({ title: t('system.common.errorTitle'), description: err.message, variant: 'destructive' }); }
                                      }
                                    }}
                                  >
                                    <option value="false">false</option>
                                    <option value="true">true</option>
                                  </select>
                                </div>
                                <div className="col-span-2">
                                  <div className="text-[10px] text-muted-foreground">User</div>
                                  <input
                                    className="w-full border rounded px-2 py-1"
                                    defaultValue={it.user}
                                    onBlur={async (e) => {
                                      const v = e.target.value;
                                      if (v && v !== it.user) {
                                        try { await api.smtpUpdate(token, it.id, { user: v }); toast({ title: t('system.common.saved') }); loadSmtp(); } catch (err) { toast({ title: t('system.common.errorTitle'), description: err.message, variant: 'destructive' }); }
                                      }
                                    }}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <div className="text-[10px] text-muted-foreground">Pass</div>
                                  <input
                                    type="password"
                                    className="w-full border rounded px-2 py-1"
                                    placeholder="(nicht anzeigen)"
                                    onBlur={async (e) => {
                                      const v = e.target.value;
                                      if (v) {
                                        try { await api.smtpUpdate(token, it.id, { pass: v }); toast({ title: t('system.common.saved') }); e.target.value = ''; } catch (err) { toast({ title: t('system.common.errorTitle'), description: err.message, variant: 'destructive' }); }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="text-[10px] text-muted-foreground">ID: {it.id} {it.companyId ? `(Org: ${it.companyId})` : '(Global)'}</div>
                              <div className="mt-2 border-t pt-2">
                                <div className="font-medium mb-1">Testmail senden</div>
                                <div className="flex gap-2 items-center">
                                  <input
                                    className="border rounded px-2 py-1 flex-1"
                                    placeholder={t('common.form.recipientEmailPlaceholder')}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.preventDefault();
                                    }}
                                    id={`smtp-test-${it.id}`}
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={async () => {
                                      const inp = document.getElementById(`smtp-test-${it.id}`);
                                      const to = inp && inp.value ? inp.value.trim() : '';
                                      if (!to) { toast({ title: t('common.pleaseEnterRecipient'), variant: 'destructive' }); return; }
                                      try {
                                        await api.smtpTest(token, it.id, { to });
                                        toast({ title: 'Testmail gesendet' });
                                      } catch (e) {
                                        toast({ title: 'Senden fehlgeschlagen', description: e.message, variant: 'destructive' });
                                      }
                                    }}
                                  >
                                    Senden
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {!smtpItems.length && !smtpLoading && (
                            <div className="text-xs text-muted-foreground">{t('system.common.none')}</div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Neue SMTP Identität</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <form
                          className="space-y-3 text-sm"
                          onSubmit={async (e) => {
                            e.preventDefault();
                            if (!smtpForm.host || !smtpForm.user || !smtpForm.pass || !smtpForm.fromEmail) {
                              toast({ title: t('common.pleaseFillRequired'), variant: 'destructive' });
                              return;
                            }
                            try {
                              setSmtpCreating(true);
                              const payload = { ...smtpForm };
                              if (!payload.companyId) payload.companyId = null;
                              await api.smtpCreate(token, payload);
                              toast({ title: 'Erstellt' });
                              setSmtpForm({ companyId: '', host: '', port: 587, secure: false, user: '', pass: '', fromEmail: '', fromName: '', enabled: true });
                              loadSmtp();
                            } catch (err) {
                              toast({ title: 'Fehler', description: err.message, variant: 'destructive' });
                            } finally {
                              setSmtpCreating(false);
                            }
                          }}
                        >
                          <div>
                            <div className="text-[10px] text-muted-foreground">Organisation ID (optional)</div>
                            <input className="w-full border rounded px-2 py-1" value={smtpForm.companyId} onChange={(e) => setSmtpForm({ ...smtpForm, companyId: e.target.value })} placeholder="leer = global" />
                          </div>
                          <div>
                            <div className="text-[10px] text-muted-foreground">Host</div>
                            <input className="w-full border rounded px-2 py-1" value={smtpForm.host} onChange={(e) => setSmtpForm({ ...smtpForm, host: e.target.value })} />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <div className="text-[10px] text-muted-foreground">Port</div>
                              <input type="number" className="w-full border rounded px-2 py-1" value={smtpForm.port} onChange={(e) => setSmtpForm({ ...smtpForm, port: parseInt(e.target.value || '587', 10) })} />
                            </div>
                            <div>
                              <div className="text-[10px] text-muted-foreground">Secure</div>
                              <select className="w-full border rounded px-2 py-1" value={smtpForm.secure ? 'true' : 'false'} onChange={(e) => setSmtpForm({ ...smtpForm, secure: e.target.value === 'true' })}>
                                <option value="false">false</option>
                                <option value="true">true</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] text-muted-foreground">User</div>
                            <input className="w-full border rounded px-2 py-1" value={smtpForm.user} onChange={(e) => setSmtpForm({ ...smtpForm, user: e.target.value })} />
                          </div>
                          <div>
                            <div className="text-[10px] text-muted-foreground">Pass</div>
                            <input type="password" className="w-full border rounded px-2 py-1" value={smtpForm.pass} onChange={(e) => setSmtpForm({ ...smtpForm, pass: e.target.value })} />
                          </div>
                          <div>
                            <div className="text-[10px] text-muted-foreground">From Email</div>
                            <input className="w-full border rounded px-2 py-1" value={smtpForm.fromEmail} onChange={(e) => setSmtpForm({ ...smtpForm, fromEmail: e.target.value })} />
                          </div>
                          <div>
                            <div className="text-[10px] text-muted-foreground">From Name (optional)</div>
                            <input className="w-full border rounded px-2 py-1" value={smtpForm.fromName} onChange={(e) => setSmtpForm({ ...smtpForm, fromName: e.target.value })} />
                          </div>
                          <div>
                            <label className="inline-flex items-center gap-2 text-xs">
                              <input type="checkbox" checked={smtpForm.enabled} onChange={(e) => setSmtpForm({ ...smtpForm, enabled: e.target.checked })} /> aktiv
                            </label>
                          </div>
                          <Button type="submit" disabled={smtpCreating}>{smtpCreating ? '...' : 'Anlegen'}</Button>
                        </form>
                      </CardContent>
                    </Card>

                  </div>
                </TabsContent>

                <TabsContent value="metaapp">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('system.metaApp.title')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm">
                        <p className="text-xs text-muted-foreground">
                          {t('system.metaApp.description')}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {t('system.metaApp.note')}
                        </p>
                        <form
                          className="space-y-3"
                          onSubmit={async (e) => {
                            e.preventDefault();
                            try {
                              setMetaAppSaving(true);
                              await api.metaAppConfigUpdate(token, {
                                appId: metaAppConfig.appId || null,
                                appSecret: metaAppSecretInput || undefined,
                              });
                              if (metaAppSecretInput) {
                                setMetaAppSecretInput("");
                              }
                              await loadMetaAppConfig();
                              toast({ title: t('system.common.saved') });
                            } catch (err) {
                              toast({ title: t('system.common.errorTitle'), description: err.message, variant: 'destructive' });
                            } finally {
                              setMetaAppSaving(false);
                            }
                          }}
                        >
                          <div>
                            <div className="text-[10px] text-muted-foreground">{t('system.metaApp.appIdLabel')}</div>
                            <input
                              className="w-full border rounded px-2 py-1"
                              value={metaAppConfig.appId}
                              onChange={(e) => setMetaAppConfig({ ...metaAppConfig, appId: e.target.value })}
                              placeholder={t('system.metaApp.appIdPlaceholder')}
                            />
                          </div>
                          <div>
                            <div className="text-[10px] text-muted-foreground">{t('system.metaApp.secretLabel')}</div>
                            <input
                              type="password"
                              className="w-full border rounded px-2 py-1"
                              value={metaAppSecretInput}
                              onChange={(e) => setMetaAppSecretInput(e.target.value)}
                              placeholder={metaAppConfig.hasSecret ? t('system.metaApp.secretPlaceholderSet') : t('system.metaApp.secretPlaceholderNew')}
                            />
                            {metaAppConfig.hasSecret && (
                              <div className="text-[10px] text-muted-foreground mt-1">
                                {t('system.metaApp.secretHintExisting')}
                              </div>
                            )}
                            {!metaAppConfig.hasSecret && (
                              <div className="text-[10px] text-muted-foreground mt-1">
                                {t('system.metaApp.secretHintNew')}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button type="submit" size="sm" disabled={metaAppSaving || metaAppLoading}>
                              {metaAppSaving ? '...' : t('common.save')}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={metaAppLoading}
                              onClick={loadMetaAppConfig}
                            >
                              {metaAppLoading ? '...' : t('status.refresh')}
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

function ImpressumEditor({ token }) {
  const { t } = useTranslation();
  const [html, setHtml] = useState("");
  const [isMarkdown, setIsMarkdown] = useState(false);
  const [quillHtml, setQuillHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const taRef = useRef(null);
  const load = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await api.systemSettingGet(token, 'impressum');
      const v = (res?.value) || "";
      setHtml(v);
      setQuillHtml(v);
    } catch (e) {
      toast({ title: 'Laden fehlgeschlagen', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [token]);
  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      // Preserve formatting from the chosen editor mode
      let rawValue = isMarkdown ? markdownToHtml(html) : (quillHtml || html);
      // Single, consistent sanitization step
      const value = sanitizeHtml(rawValue);
      await api.systemSettingSet(token, 'impressum', value);
      setHtml(value);
      setQuillHtml(value);
      toast({ title: 'Gespeichert' });
    } catch (e) {
      toast({ title: 'Fehler', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-muted-foreground">Impressum – Eingabe</div>
        <label className="text-xs inline-flex items-center gap-2">
          <input type="checkbox" checked={isMarkdown} onChange={(e)=>setIsMarkdown(e.target.checked)} /> Markdown-Eingabe
        </label>
      </div>
      {isMarkdown ? (
        <>
          <div className="flex flex-wrap gap-2 text-xs">
            <Button type="button" size="xs" variant="outline" onClick={() => wrapSelection("**","**","fett")}>B</Button>
            <Button type="button" size="xs" variant="outline" onClick={() => wrapSelection("*","*","kursiv")}>I</Button>
            <Button type="button" size="xs" variant="outline" onClick={() => insertLink()}>Link</Button>
            <Button type="button" size="xs" variant="outline" onClick={() => toggleLinePrefix("# ")}>H1</Button>
            <Button type="button" size="xs" variant="outline" onClick={() => toggleLinePrefix("## ")}>H2</Button>
            <Button type="button" size="xs" variant="outline" onClick={() => toggleLinePrefix("- ")}>Liste</Button>
          </div>
          <textarea
            className="w-full border rounded px-2 py-1 min-h-[180px] font-mono"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            ref={taRef}
            placeholder={'# Unternehmen GmbH\nStraße 1\n12345 Stadt\n\n[Kontakt](mailto:info@example.com)'}
          />
        </>
      ) : (
        <ReactQuill
          theme="snow"
          value={quillHtml}
          onChange={setQuillHtml}
          modules={{
            toolbar: [
              [{ header: [1, 2, 3, false] }],
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link'],
              ['clean']
            ],
          }}
          formats={['header','bold','italic','underline','list','bullet','link']}
        />
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" type="submit" disabled={saving}>{saving ? '...' : t('common.save')}</Button>
        <Button size="sm" variant="outline" type="button" onClick={load} disabled={loading}>{loading ? '...' : t('status.refresh')}</Button>
      </div>
      <div className="text-[10px] text-muted-foreground">Hinweis: Der Link ist öffentlich unter /impressum sichtbar.</div>
      <div className="pt-2 border-t">
        <div className="text-[10px] text-muted-foreground mb-1">Vorschau</div>
        <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(isMarkdown ? markdownToHtml(html) : (quillHtml || html)) }} />
      </div>
    </form>
  );

  function wrapSelection(prefix, suffix, fallback = "Text") {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const before = html.slice(0, start);
    const sel = html.slice(start, end);
    const after = html.slice(end);
    const inner = sel || fallback;
    const next = before + prefix + inner + suffix + after;
    setHtml(next);
    queueMicrotask(() => {
      try {
        ta.focus();
        const from = before.length + prefix.length;
        const to = from + inner.length;
        ta.setSelectionRange(from, to);
      } catch {}
    });
  }

  function toggleLinePrefix(marker) {
    const ta = taRef.current;
    if (!ta) return;
    const pos = ta.selectionStart ?? 0;
    // find start of current line
    const lineStart = html.lastIndexOf("\n", Math.max(0, pos - 1)) + 1;
    const lineEndIdx = html.indexOf("\n", pos);
    const lineEnd = lineEndIdx === -1 ? html.length : lineEndIdx;
    const line = html.slice(lineStart, lineEnd);
    let replaced;
    if (line.startsWith(marker)) {
      replaced = html.slice(0, lineStart) + line.slice(marker.length) + html.slice(lineEnd);
    } else {
      replaced = html.slice(0, lineStart) + marker + line + html.slice(lineEnd);
    }
    setHtml(replaced);
    queueMicrotask(() => { try { ta.focus(); ta.setSelectionRange(pos + (line.startsWith(marker) ? -marker.length : marker.length), pos + (line.startsWith(marker) ? -marker.length : marker.length)); } catch {} });
  }

  function insertLink() {
    const ta = taRef.current; if (!ta) return;
    const start = ta.selectionStart ?? 0; const end = ta.selectionEnd ?? 0;
    const sel = html.slice(start, end) || 'Linktext';
    const url = window.prompt('Ziel-URL (https://, http:// oder mailto:)', 'https://');
    if (url === null) return; // cancelled
    const safe = /^(https?:|mailto:)/i.test(url) ? url : '#';
    wrapSelection('[', `](${safe})`, sel);
  }
}

function DatenschutzEditor({ token }) {
  const { t } = useTranslation();
  const [html, setHtml] = useState("");
  const [isMarkdown, setIsMarkdown] = useState(false);
  const [quillHtml, setQuillHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const taRef = useRef(null);
  const load = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await api.systemSettingGet(token, 'datenschutz');
      const v = (res?.value) || "";
      setHtml(v);
      setQuillHtml(v);
    } catch (e) {
      toast({ title: 'Laden fehlgeschlagen', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [token]);
  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      let rawValue = isMarkdown ? markdownToHtml(html) : (quillHtml || html);
      const value = sanitizeHtml(rawValue);
      await api.systemSettingSet(token, 'datenschutz', value);
      setHtml(value);
      setQuillHtml(value);
      toast({ title: 'Gespeichert' });
    } catch (e) {
      toast({ title: 'Fehler', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-muted-foreground">Datenschutz – Eingabe</div>
        <label className="text-xs inline-flex items-center gap-2">
          <input type="checkbox" checked={isMarkdown} onChange={(e)=>setIsMarkdown(e.target.checked)} /> Markdown-Eingabe
        </label>
      </div>
      {isMarkdown ? (
        <>
          <div className="flex flex-wrap gap-2 text-xs">
            <Button type="button" size="xs" variant="outline" onClick={() => wrapSelection("**","**","fett")}>B</Button>
            <Button type="button" size="xs" variant="outline" onClick={() => wrapSelection("*","*","kursiv")}>I</Button>
            <Button type="button" size="xs" variant="outline" onClick={() => insertLink()}>Link</Button>
            <Button type="button" size="xs" variant="outline" onClick={() => toggleLinePrefix("# ")}>H1</Button>
            <Button type="button" size="xs" variant="outline" onClick={() => toggleLinePrefix("## ")}>H2</Button>
            <Button type="button" size="xs" variant="outline" onClick={() => toggleLinePrefix("- ")}>Liste</Button>
          </div>
          <textarea
            className="w-full border rounded px-2 py-1 min-h-[180px] font-mono"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            ref={taRef}
            placeholder={'# Datenschutz\nWir nehmen den Schutz Ihrer Daten ernst...'}
          />
        </>
      ) : (
        <ReactQuill
          theme="snow"
          value={quillHtml}
          onChange={setQuillHtml}
          modules={{
            toolbar: [
              [{ header: [1, 2, 3, false] }],
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link'],
              ['clean']
            ],
          }}
          formats={['header','bold','italic','underline','list','bullet','link']}
        />
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" type="submit" disabled={saving}>{saving ? '...' : t('common.save')}</Button>
        <Button size="sm" variant="outline" type="button" onClick={load} disabled={loading}>{loading ? '...' : t('status.refresh')}</Button>
      </div>
      <div className="text-[10px] text-muted-foreground">Hinweis: Der Link ist öffentlich unter /datenschutz sichtbar.</div>
      <div className="pt-2 border-t">
        <div className="text-[10px] text-muted-foreground mb-1">Vorschau</div>
        <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(isMarkdown ? markdownToHtml(html) : (quillHtml || html)) }} />
      </div>
    </form>
  );

  function wrapSelection(prefix, suffix, fallback = "Text") {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const before = html.slice(0, start);
    const sel = html.slice(start, end);
    const after = html.slice(end);
    const inner = sel || fallback;
    const next = before + prefix + inner + suffix + after;
    setHtml(next);
    queueMicrotask(() => {
      try {
        ta.focus();
        const from = before.length + prefix.length;
        const to = from + inner.length;
        ta.setSelectionRange(from, to);
      } catch {}
    });
  }

  function toggleLinePrefix(marker) {
    const ta = taRef.current;
    if (!ta) return;
    const pos = ta.selectionStart ?? 0;
    const lineStart = html.lastIndexOf("\n", Math.max(0, pos - 1)) + 1;
    const lineEndIdx = html.indexOf("\n", pos);
    const lineEnd = lineEndIdx === -1 ? html.length : lineEndIdx;
    const line = html.slice(lineStart, lineEnd);
    let replaced;
    if (line.startsWith(marker)) {
      replaced = html.slice(0, lineStart) + line.slice(marker.length) + html.slice(lineEnd);
    } else {
      replaced = html.slice(0, lineStart) + marker + line + html.slice(lineEnd);
    }
    setHtml(replaced);
    queueMicrotask(() => { try { ta.focus(); ta.setSelectionRange(pos + (line.startsWith(marker) ? -marker.length : marker.length), pos + (line.startsWith(marker) ? -marker.length : marker.length)); } catch {} });
  }

  function insertLink() {
    const ta = taRef.current; if (!ta) return;
    const start = ta.selectionStart ?? 0; const end = ta.selectionEnd ?? 0;
    const sel = html.slice(start, end) || 'Linktext';
    const url = window.prompt('Ziel-URL (https://, http:// oder mailto:)', 'https://');
    if (url === null) return;
    const safe = /^(https?:|mailto:)/i.test(url) ? url : '#';
    wrapSelection('[', `](${safe})`, sel);
  }
}

function EmailTemplateEditor({ token, keyName, label }) {
  const { t } = useTranslation();
  const [companyId, setCompanyId] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [footer, setFooter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await api.emailTemplateGet(token, keyName, companyId || null).catch(() => null);
      if (res?.item) {
        setSubject(res.item.subject || "");
        setHtml(res.item.html || "");
        setFooter(res.item.footer || "");
      } else {
        setSubject(""); setHtml(""); setFooter("");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [token, keyName, companyId]);

  const save = async (e) => {
    e.preventDefault();
    if (!subject || !html) { toast({ title: 'Bitte Subject und HTML angeben', variant: 'destructive' }); return; }
    try {
      setSaving(true);
      await api.emailTemplateUpsert(token, keyName, {
        companyId: companyId || null,
        subject,
        html,
        footer: footer || null,
      });
      toast({ title: 'Gespeichert' });
    } catch (e) {
      toast({ title: 'Fehler', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="font-medium">{label}</div>
      <div className="grid grid-cols-3 gap-2 items-center">
        <div className="text-[10px] text-muted-foreground">Organisation ID (optional)</div>
        <input className="col-span-2 border rounded px-2 py-1" value={companyId} onChange={(e) => setCompanyId(e.target.value)} placeholder="leer = global" />
      </div>
      <div>
        <div className="text-[10px] text-muted-foreground">Betreff</div>
        <input className="w-full border rounded px-2 py-1" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div>
        <div className="text-[10px] text-muted-foreground">HTML</div>
        <textarea className="w-full border rounded px-2 py-1 min-h-[140px] font-mono" value={html} onChange={(e) => setHtml(e.target.value)} placeholder="Platzhalter: {{code}}, {{claimUrl}}, {{verifyUrl}}, {{actionTitle}}, {{companyName}}, {{campaignName}}, {{discountType}}, {{discountValue}}" />
      </div>
      <div>
        <div className="text-[10px] text-muted-foreground">Footer (optional)</div>
        <textarea className="w-full border rounded px-2 py-1 min-h-[60px]" value={footer} onChange={(e) => setFooter(e.target.value)} placeholder="z.B. Impressum, Datenschutz, Kontakt" />
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={saving}>{saving ? '...' : t('common.save')}</Button>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}>{loading ? '...' : t('status.refresh')}</Button>
      </div>
    </div>
  );
}

function EmailTemplateTester({ token }) {
  const { t } = useTranslation();
  const [to, setTo] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [keyName, setKeyName] = useState("code");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!to.trim()) { toast({ title: t('common.recipientMissing'), variant: 'destructive' }); return; }
    try {
      setSending(true);
      await api.emailTemplateTest(token, keyName, { to, companyId: companyId || null, sample: {
        code: 'ABC123',
        claimUrl: 'https://example.com/claim/ABC123',
        verifyUrl: 'https://example.com/verify/xyz',
        actionTitle: 'Aktionstitel',
        companyName: 'Unternehmen GmbH',
        campaignName: 'Kampagne 2025',
        discountType: 'PERCENT',
        discountValue: 30,
      }});
      toast({ title: 'Test gesendet' });
    } catch (e) {
      toast({ title: 'Fehler', description: e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="space-y-2 text-sm">
      <div className="grid grid-cols-3 gap-2 items-center">
        <div className="text-[10px] text-muted-foreground">Template</div>
        <select className="col-span-2 border rounded px-2 py-1" value={keyName} onChange={(e) => setKeyName(e.target.value)}>
          <option value="verify">verify</option>
          <option value="code">code</option>
        </select>
      </div>
      <div className="grid grid-cols-3 gap-2 items-center">
        <div className="text-[10px] text-muted-foreground">Organisation ID (optional)</div>
        <input className="col-span-2 border rounded px-2 py-1" value={companyId} onChange={(e) => setCompanyId(e.target.value)} placeholder="leer = global" />
      </div>
      <div className="grid grid-cols-3 gap-2 items-center">
        <div className="text-[10px] text-muted-foreground">Empfänger</div>
        <input className="col-span-2 border rounded px-2 py-1" value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com" />
      </div>
      <Button size="sm" onClick={send} disabled={sending}>{sending ? '...' : 'Test senden'}</Button>
    </div>
  );
}

export default SystemDashboard;
